import React, { useEffect, useRef, useState } from "react";
import { Modal } from "@mui/material";
import PropTypes from "prop-types";
import dayjs from "dayjs";

function Camera(props) {
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);

  const ref = useRef(null);

  const { open, onClose, onComplete } = props;

  useEffect(() => {
    if (open) {
      openCamera();
    } else {
      closeCamera();
    }
  }, [open]);

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setMediaStream(stream);
      ref.current.srcObject = stream;
    } catch (error) {
      console.error("Error accessing the camera:", error);
    }
  };

  const closeCamera = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      setMediaStream(null);
    }
  };

  const getBase64Image = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setCapturedImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const captureImage = () => {
    const canvas = document.createElement("canvas");
    canvas.width = ref.current.videoWidth;
    canvas.height = ref.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(ref.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      const file = new File(
        [blob],
        `signature_capture_${dayjs().format("MM_DD_YYYY_hh:mm:ss")}.jpg`,
        { type: "image/jpg" }
      );
      getBase64Image(file);
      const reader = new FileReader();

      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0, img.width, img.height);
          const optimizedImage = canvas.toDataURL("image/jpeg", 0.7); // Adjust quality as needed (0.7 for 70% quality)
          setCapturedFile({name: file.name, file:optimizedImage});
        };
      };

      reader.readAsDataURL(file);
    }, "image/jpg");
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setCapturedFile(null);
  };

  const handleSubmit = () => {
    onComplete(capturedFile);
    setCapturedImage(null);
    setCapturedFile(null);
    onClose();
  };

  let photoPreview = null;
  if (capturedImage) {
    photoPreview = (
      <div className="flex flex-col absolute top-0 bottom-0 left-0 right-0 bg-white z-10">
        <img
          src={capturedImage}
          className="flex-1 object-cover mt-auto"
          alt="capture"
        />
        <div className="flex justify-between items-center p-3 bg-black h-[75px] mt-auto">
          <button className="text-white" onClick={handleRetake}>
            Retake
          </button>
          <button className="text-white" onClick={handleSubmit}>
            Use Photo
          </button>
        </div>
      </div>
    );
  }

  return (
    <Modal
      open={open}
      className="flex backdrop-blur-md bg-opacity-20 overflow-y-scroll"
    >
      <div className="sm:w-[500px] h-full sm:h-fit w-full bg-black m-auto relative flex flex-col justify-center">
        {photoPreview}
        <video
          ref={ref}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover mt-auto"
        ></video>
        <div className="flex justify-between items-center p-3 bg-black h-[75px] mt-auto">
          <button className="text-white" onClick={onClose}>
            Cancel
          </button>
          <button
            className="w-12 h-12 border-2 rounded-full p-1"
            onClick={captureImage}
          >
            <div className="w-full h-full bg-white rounded-full" />
          </button>
          <button className="invisible">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

Camera.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default Camera;
